/**
 * @file: useChatPresences.ts
 *
 * Owns the viewer's presence set — the online identities the UI renders as
 * green dots:
 *   - `presence$` — the live set, keyed by `presenceKeyOf`.
 *   - `refreshPresence(refs)` — snapshot the online state for a set of
 *     identities and fold it in (ADD-ONLY; see below).
 *   - a live subscription that folds each `presence` flip (online/offline)
 *     arriving on the viewer's own user channel into the set.
 * The set is wiped on account switch so a new viewer never inherits the
 * previous one's dots.
 */

import { UserPresenceRef } from '@euler/model/chat';
import { IdType } from '@euler/model/common';
import { useServiceFactory } from '@euler/services/factory';
import { idEquals } from '@euler/utils';
import { useCreation, useWatchValue } from '@euler/utils/hooks';
import { useCallback, useEffect, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ChatWSClient } from '../client';
import { presenceKeyOf } from '../presence';

export interface ChatPresences {
  /** Online identities, keyed by `presenceKeyOf`. */
  presence$: BehaviorSubject<ReadonlySet<string>>;
  /** Snapshot online state for `refs` and fold it in (add-only). */
  refreshPresence: (refs: UserPresenceRef[]) => void;
}

export function useChatPresences(args: {
  wsClient: ChatWSClient;
  myPlatformUserId: IdType;
}): ChatPresences {
  const { wsClient, myPlatformUserId } = args;
  const { userService } = useServiceFactory();

  const presence$ = useCreation(
    () => new BehaviorSubject<ReadonlySet<string>>(new Set()),
    [],
  );

  // The live viewer id, read by in-flight async work to detect an account
  // switch that landed mid-request.
  const myPlatformUserIdRef = useRef(myPlatformUserId);
  myPlatformUserIdRef.current = myPlatformUserId;

  // Snapshot the online state for a set of identities and fold it into the
  // presence set. ADD-ONLY: it only adds the refs the snapshot reports online,
  // never removes. A snapshot is taken at query time, so removing "not in
  // snapshot" refs would clobber a live `online` event that lands during the
  // round-trip. Going offline is driven by the live `presence` event (the
  // server's keyspace-expiry path), not by this query.
  const refreshPresence = useCallback(
    (refs: UserPresenceRef[]) => {
      if (refs.length === 0) return;
      const requestedFor = myPlatformUserIdRef.current;
      userService
        .queryPresence(refs)
        .then(({ online }) => {
          // An account switch that landed during the round-trip already reset
          // `presence$`; dropping the stale snapshot avoids re-adding the
          // previous viewer's online dots.
          if (!idEquals(myPlatformUserIdRef.current, requestedFor)) return;
          if (online.length === 0) return;
          const cur = presence$.getValue();
          const next = new Set(cur);
          let changed = false;
          for (const ref of online) {
            const key = presenceKeyOf(ref);
            if (!next.has(key)) {
              next.add(key);
              changed = true;
            }
          }
          if (changed) presence$.next(next);
        })
        .catch(e => console.error('[chat-presence] query failed', e));
    },
    [userService, presence$],
  );

  // Live presence flips arrive on the viewer's own user channel (non-room
  // scoped). Fold each into the set; going online adds, offline removes.
  useEffect(() => {
    if (!myPlatformUserId) return;
    const sub = wsClient.onKind('presence').subscribe(ev => {
      const key = presenceKeyOf({
        platformUserId: ev.platformUserId,
        appId: ev.appId,
        appScope: ev.appScope,
      });
      const isOnline = ev.body.state === 'online';
      const cur = presence$.getValue();
      if (isOnline === cur.has(key)) return;
      const next = new Set(cur);
      if (isOnline) next.add(key);
      else next.delete(key);
      presence$.next(next);
    });
    return () => sub.unsubscribe();
  }, [myPlatformUserId, wsClient, presence$]);

  // Account switch within the same auth-context key — wipe the set so a new
  // viewer never inherits the previous one's dots. `!prev` skips mount and
  // the initial login.
  useWatchValue(myPlatformUserId, prev => {
    if (!prev) return;
    presence$.next(new Set());
  });

  return { presence$, refreshPresence };
}
